import { createBrowserRouter } from 'react-router';
import { useAuthStore } from '@/entities/auth';
import { parseApiError, queryClient } from '@/shared/api';
import { getSessionNode, initPushBridge } from '@/shared/bridge';
import { createNavigator, isSelfLoopPathname, type Navigator } from '@/shared/navigation';
import { closeAllOverlays } from '@/shared/overlay';
import { isVersionBlocked } from '@/shared/version';
import { useDebugStore } from '@/shared/stores';
import { ROUTES } from '@/shared/routing';
import { createRouteTree } from '@/app/routing/app-routes';
import { createNavigationGuards } from '@/app/routing/navigation.guard';
import { initAnalytics } from './analytics';
import { createApiAppLegacy } from './api-app-legacy';
import { initAuthSync } from './auth-sync';
import { createApiV2 } from './api-v2';
import { initDocumentTitle } from './document-title';
import { createApiWebLegacy } from './api-web-legacy';
import { createFcmSync } from './fcm-sync';
import { initNetworkSync } from './network-sync';
import { initWebPush } from './web-push';
import type { ApiCallbacks } from './api-callbacks';

type AppRouter = ReturnType<typeof createBrowserRouter>;

/**
 * 런타임 연결의 SSOT. guards · router · navigator · api 를 한 곳에서 합성한다.
 *
 * 각 모듈은 자기들끼리 직접 import 하지 않는다. 다 순수 factory 고, 본 파일이 콜백과
 * deps 를 주입한다. 모듈 순환 import 와 TDZ 위험을 차단하는 게 목적.
 *
 *
 * # 의존 순서 — 단방향 사슬
 *
 *   guards     ← 의존 없음
 *   router     ← guards 의존
 *   navigator  ← router 의존
 *   api        ← navigator 의존 (콜백 안에서 사용)
 *
 * 단방향이라 위→아래 평가만으로 안전. 콜백은 모듈 평가 후 (네트워크 응답·사용자
 * 인터랙션 시점) 발화하므로 그때는 모든 export 가 채워진 뒤다.
 *
 *
 * 관련 ADR: composition-root-runtime-wiring, navigator-service.
 */

const guards = createNavigationGuards();

export const router: AppRouter = createBrowserRouter(createRouteTree(guards));

export const appNavigator: Navigator = createNavigator({ router });

// 라우트 이동 시 열린 overlay 를 전부 닫는다 — 명령형 overlay 는 페이지 스코프(라우트를 넘겨
// 살아남지 않는다). OverlayHost 는 RouterProvider 밖(AppProvider)이라 useLocation 을 못 써서
// 여기서 router 인스턴스를 직접 구독한다. (세션 만료 redirect 시 모달 잔존 → 무한 refetch 루프
// 차단은 onAuthError 가 clear 전에 직접 닫아 처리 — 아래.)
let lastOverlayPathname = router.state.location.pathname;
router.subscribe((state) => {
  if (state.location.pathname !== lastOverlayPathname) {
    lastOverlayPathname = state.location.pathname;
    closeAllOverlays();
  }
});

/**
 * 세션 종료 단일 경로 — 토큰·캐시·화면을 한 순서로 정리하고 로그인으로 보낸다.
 * 진입 사유는 둘: 이 탭의 401(`onAuthError`, reason=session_expired) / 다른 탭의 로그아웃(`initAuthSync`).
 *
 * ⚠️ **토큰 정리(`clear`)는 남은 세션이 있을 때만** — 탭 간 무한 핑퐁 차단점이다.
 * `clear()` 는 storage 를 **두 번** 건드린다: persist 가 `set` 을 받아 `{null,null}` JSON 을 쓰고(=
 * `newValue≠null` 이벤트), 이어서 `clearStorage()` 가 키를 지운다(= `newValue===null` 이벤트).
 * 즉 빈 세션에서 또 부르면 없던 키를 만들었다 지우며 **다른 탭의 `onRemoteSignOut` 을 깨운다**.
 * 그 탭이 다시 `endSession` → `clear` → 이벤트 2개… 로 되받아 두 탭이 서로를 영원히 로그아웃시킨다.
 * 증상: 한쪽 탭 로그아웃 후 다른 탭에서 로그인하면 **로그인 API 는 성공**하는데 루프의 `queryClient.clear()`
 * 가 진입 쿼리를 계속 취소하고 `goToLogin()` 이 되돌려 로그인 페이지에 갇힌다(새로고침하면 그 탭이
 * 이벤트에 응답하지 않게 되어 사슬이 끊기고 정상 진입).
 *
 * 화면 정리(`queryClient.clear` · `goToLogin`)는 **가드 밖**이라 항상 수행한다 — 토큰만 이미 비어 있고
 * 화면은 그대로인 상태(다른 탭발 rehydrate 직후)에서도 로그인으로 내보내야 하기 때문.
 */
const endSession = (reason?: 'session_expired') => {
  // overlay(모달 등)를 **먼저** 닫는다 — 닫지 않으면 아래 queryClient.clear() 가 아직 mount 된
  // 모달 내부 query observer 를 재구독→refetch→401 로 되돌려 무한 로딩에 빠진다. clear 보다 앞서야
  // observer 가 파괴된 뒤 캐시가 비워진다. (route 구독은 navigate 시점이라 clear 보다 늦음.)
  closeAllOverlays();
  const { accessToken, refreshToken } = useAuthStore.getState();
  if (accessToken || refreshToken) useAuthStore.getState().clear();
  queryClient.clear();
  // 로그인 페이지로 navigate (`reason` 은 안내 카피 분기용). URL 조립은 navigator 가 담당.
  //
  // 현재 페이지 경로를 redirectUrl 로 캡처해 navigator 에 넘긴다. router 접근이
  // 여기서만 가능해서 캡처도 여기서 한다.
  //
  // 단 사용자가 이미 self-loop 영역 (`/auth/*` 또는 `/error/*`) 에 있으면 캡처를 건너뛴다.
  // 이미 auth 흐름 안이라 그 위치로 다시 보낼 이유가 없다. 판정 룰은 `isSelfLoopPathname`.
  const { pathname, search } = router.state.location;
  if (isSelfLoopPathname(pathname)) return;
  appNavigator.goToLogin({ reason, redirectUrl: `${pathname}${search}` });
};

const apiCallbacks: ApiCallbacks = {
  onAuthError: () => endSession('session_expired'),
  onMaintenance: () => {
    if (router.state.location.pathname !== ROUTES.ERROR_MAINTENANCE) {
      appNavigator.replace(ROUTES.ERROR_MAINTENANCE);
    }
  },
  onNetworkError: () => {
    // TODO(error-handling): Sentry + useAlert 도입 후 교체 — docs/open-issues.md §2
    console.error('[TODO:error-handling]', { where: 'api.network' });
  },
  onServerError: (status) => {
    // TODO(error-handling): Sentry + useAlert 도입 후 교체 — docs/open-issues.md §2
    console.error('[TODO:error-handling]', { where: 'api.server', status });
  },
};

// ---------------------------------------------------------------------------
// 공통 access 재발급 (single-flight) — 세 API(app-legacy·v2·web-legacy)가 이 함수 하나를 공유한다.
//
// 이전엔 app-legacy 가 refresh 를 내부 소유하고 v2·web-legacy 가 `reissueAccess` 에 위임했는데,
// refresh 오케스트레이션(single-flight·토큰 저장)을 여기로 끌어올려 SSOT 화했다. 패키지는 401 시
// 주입된 이 함수만 호출(덤)하고, 실제 refresh HTTP·rotation·저장은 전부 여기서 일어난다.
//
//   - single-flight: `inFlight` 로 동시 401 을 1회 refresh 로 coalesce (rotation "첫 호출만 유효" 정합).
//   - refresh HTTP 는 app-legacy 클라이언트(`apiAppLegacy.auth.refreshToken`)로 — auth 가 v3(/api/v3) 라.
//     `Authorization: Bearer <refresh>` 헤더도 여기서 refresh 토큰을 인자로 넘겨 부여한다(transport 는 refresh 특별 취급 안 함).
//   - `apiAppLegacy` 를 클로저로 late-bind — 아래 생성보다 먼저 정의되지만 호출은 네트워크 응답 시점이라 TDZ 안전.
//
// # 세션 종료 판정도 **여기가 소유**한다 (인터셉터가 아니라)
// 반환값 `null` 은 "재발급 불가 → 원 요청 실패"일 뿐이고, 로그아웃 여부는 이 함수가 직접 정한다. 이유 둘:
//   ① **사유를 여기서만 안다.** 인터셉터는 `null` 만 보므로 401(세션 만료)과 5xx·타임아웃(판정 불가)을
//      구분할 수 없다. 배포 중 503 이나 auth 서비스 순간 장애로 로그아웃되던 원인이 이것이었다.
//   ② **1회만 통보된다.** single-flight 라 refresh 는 1회 도는데, 대기하던 요청 N개가 각자 `onAuthError`
//      를 부르면 세션 종료가 N번 실행됐다(대시보드 병렬 쿼리 6개 = endSession 6회).
// 그래서 세션이 **확실히 죽은 경우에만** `endSession` 하고, 판정 불가면 세션을 유지한 채 요청만 실패시킨다.
// ---------------------------------------------------------------------------
/**
 * 재발급 실패가 **세션 사망 확정**인가 — `endSession` 여부의 유일한 판정자.
 *
 * 채택: BE 가 **우리 토큰을 거절**한 코드만(400 잘못된 형식 · 401 만료·회전소모 · 403 권한 상실).
 * 서버는 정상 동작 중이고 같은 토큰으론 재시도해도 결과가 같으니 세션을 끝내는 게 맞다.
 *
 * 제외 — 4xx 라도 아래는 **재시도 여지가 있거나 우리 잘못이 아니다**:
 *   - `429`(레이트리밋) — 잠시 후면 되는데 끊으면 손해. 트래픽 몰릴 때라 **다수가 동시에 튕긴다**.
 *   - `408`(요청 타임아웃) — 성격이 5xx 와 같다.
 *   - `404`·`405`(경로·메서드 없음) — 배포/라우팅 사고. BE 설정 하나로 **전 사용자 강제 로그아웃**이 된다.
 *   - `5xx`·`0`(네트워크·타임아웃 — `parseApiError` 가 응답 없으면 0) — 세션 생사 판정 불가.
 *
 * 판정이 **비대칭**이라 이렇게 좁힌다: 잘못 로그아웃시키면 사용자가 즉시 피해를 보지만, 로그아웃을
 * 놓치면 다음 401 이나 재부팅에서 자연히 정리된다. 애매하면 유지 쪽.
 */
const SESSION_REJECTED_STATUS = new Set([400, 401, 403]);
const isSessionRejected = (error: unknown): boolean =>
  SESSION_REJECTED_STATUS.has(parseApiError(error).status);

let refreshInFlight: Promise<string | null> | null = null;
const refreshToken = (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    // 강제 업데이트 상태면 재발급을 시도조차 하지 않는다 — 하위호환이 끊긴 버전이라 토큰을 갱신해도
    // 쓸 데가 없고, 실패 시 세션 종료 판정이 업데이트 화면 밖으로 끌어낸다. `endSession` 도 부르지 않는다.
    if (isVersionBlocked()) return null;
    const refresh = useAuthStore.getState().refreshToken;
    // 재발급 재료가 아예 없다 = 세션 없음(확정). 인터셉터가 안 끝내주므로 여기서 끝낸다.
    if (!refresh) {
      endSession('session_expired');
      return null;
    }
    try {
      const { auth } = await apiAppLegacy.auth.refreshToken({ session: getSessionNode() }, refresh);
      useAuthStore.getState().setTokens({ access: auth.accessToken, refresh: auth.refreshToken });
      return auth.accessToken;
    } catch (error) {
      // 탭 경합 구제 — single-flight 는 **이 탭 안에서만** 유효해서, 다른 탭이 같은 순간 먼저 회전시켰으면
      // 우리가 쓴 refresh 는 이미 소모된 값이라 여기로 떨어진다(rotation = 첫 호출만 유효). `storage` 이벤트
      // (auth-sync)는 비동기라 아직 안 왔을 수 있으니 저장소를 직접 다시 읽는다. refresh 가 바뀌어 있으면
      // 그 탭이 방금 재발급을 끝냈다는 뜻 — 저장된 access 를 그대로 쓰면 되고, 재발급을 또 할 필요가 없다
      // (새 refresh 를 여기서 소모하면 상대 탭을 같은 방식으로 끊는다).
      await useAuthStore.persist.rehydrate();
      const { accessToken, refreshToken: rotated } = useAuthStore.getState();
      if (accessToken && rotated && rotated !== refresh) return accessToken;

      if (isSessionRejected(error)) endSession('session_expired');
      return null;
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

// v3 — auth·user 등 현행 도메인 (refresh HTTP 의 소유 클라이언트).
export const apiAppLegacy = createApiAppLegacy(apiCallbacks, refreshToken);

// v2 — 병행 데이터 도메인(/api/v2). 같은 콜백·공통 refreshToken 공유.
export const apiV2 = createApiV2(apiCallbacks, refreshToken);

// web-legacy — 레거시 데이터 도메인(/api/2.0, v2 와 동일 BE). 동일 콜백·토큰·공통 refreshToken.
export const apiWebLegacy = createApiWebLegacy(apiCallbacks, refreshToken);

// ---------------------------------------------------------------------------
// 온라인 상태 동기화 — 브라우저 online/offline 이벤트 + 네이티브 `network.change` 브릿지 메시지를
// 단일 코디네이터(network-sync)로 합류시켜 TanStack `onlineManager` 에 물린다. api 의존이 없어
// 배선 순서 무관하지만, 부팅 직후부터 신호를 받도록 앞쪽에 둔다(오프라인 진입 토스트 포함).
// ---------------------------------------------------------------------------
initNetworkSync();

// ---------------------------------------------------------------------------
// 탭 간 auth 동기화 — 다른 탭이 토큰을 갱신하면 `storage` 이벤트로 이 탭의 store 를 최신화한다.
// 없으면 refresh rotation 이 탭끼리 충돌해 한쪽 탭의 정상 갱신이 다른 탭을 로그아웃시킨다(auth-sync 주석).
// api 의존이 없어 배선 순서는 자유 — 부팅 직후부터 듣도록 앞쪽에 둔다.
// ---------------------------------------------------------------------------
initAuthSync({ onRemoteSignOut: () => endSession() });

// ---------------------------------------------------------------------------
// 문서 제목 동기화 — `헤빛온 · <화면명>`. **GA 보다 먼저** 배선해야 한다: 라우터 구독자는 등록
// 순서대로 호출되므로 뒤에 두면 GA 가 직전 화면의 제목을 `dt` 로 실어 보낸다.
// ---------------------------------------------------------------------------
initDocumentTitle({ router });

// ---------------------------------------------------------------------------
// GA4 — 초기화 + 라우트 전환 → page_view. 브라우저·WebView(앱)를 **같은 방식으로** 잰다
// (빌드 타깃으로 가르면 세션 정의가 갈려 웹↔앱 비교가 무의미해진다). api 의존이 없고
// `env.gaMeasurementId` 미설정 환경에선 통째로 no-op 이라 배선 순서는 자유.
// ---------------------------------------------------------------------------
initAnalytics({ router });

// ---------------------------------------------------------------------------
// FCM 토큰 동기화 — 토큰 변경 + access token 생성(인증)의 두 트리거를 단일 코디네이터
// (fcm-sync)로 통합해 `POST /api/v3/auth/fcm` 등록(로그인 body 대체). api(registerFcm)
// 의존이라 apiAppLegacy 생성 후 배선. 토큰 공급원은 환경별 상호배타:
//   native(WebView) = bridge push.tokenIssued / web(브라우저) = FCM JS SDK(web-push).
// 둘 다 fcmSync.setFcmToken 으로 합류. 관련 ADR: push-notifications.
// ---------------------------------------------------------------------------
const fcmSync = createFcmSync({ registerFcm: apiAppLegacy.auth.registerFcm });
// FCM 토큰 합류점 — BE 등록(fcmSync) + 비-prod 디버그 패널 노출용 store 미러링을 함께 태운다.
const onFcmToken = (fcmToken: string) => {
  useDebugStore.getState().setFcmToken(fcmToken);
  fcmSync.setFcmToken(fcmToken);
};
initPushBridge({ navigator: appNavigator, onFcmToken });
initWebPush({ navigator: appNavigator, onFcmToken });
useAuthStore.subscribe((state, prev) => {
  if (state.accessToken === prev.accessToken) return;
  // access token 이 **바뀌면** 등록 시도, 사라지면(로그아웃) 리셋.
  //
  // 로그인 전이(`null→v`)뿐 아니라 **재발급 전이(`v→v'`)도 트리거**한다 — 앞선 등록이 만료 토큰으로 401 을
  // 맞고 실패했을 때, 다른 요청이 일으킨 재발급에 편승해 복구하기 위해서다. `null→v` 만 보던 시절엔 그
  // 트리거가 영영 오지 않아 재로그인 전까지 푸시를 못 받았다(`docs/API-ISSUES.md` "FCM 토큰 등록 유실").
  // 성공 후 재전송은 `sync()` 의 `fcmToken === lastSentFcmToken` 가드가 막으므로 추가 비용은 없다.
  if (state.accessToken) {
    fcmSync.onAuthenticated();
  } else {
    fcmSync.reset();
  }
});

// 버전 체크는 여기서 배선하지 않는다 — `widgets/version-update-gate` 가 소유한다(`AppProvider` 상시 마운트).
// 과거엔 "UI 가 아직 없어서" composition 이 `QueryObserver` 로 직접 구독해 콘솔로만 관측했는데, 게이트 위젯이
// 생기면서 그 명분(활성 observer 유지 → `refetchOnWindowFocus` 성립)도 로깅도 위젯으로 흡수했다.
